import { AnySchema } from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import { useDebounceByHash, UseDebounceByHashReturn } from "@/lib/utils"
import {
  cloneDeep,
  get,
  isNaN,
  isPlainObject,
  merge,
  mergeWith,
  set,
  toPath,
} from "lodash"
import React, { useEffect, useRef, useState } from "react"
import { Schema, ValidationError } from "yup"
import { readonly, ref, watch } from "@vue/reactivity"
import { isFilled } from "@/ipqc/lib/utils"

export type UseFormProps = {
  defaultValues?: any
  metadata?: any
  validator?: Schema
  context?: Record<string, any>
}

export type SetValueOptions = {
  shouldDirty?: boolean
  shouldTouch?: boolean
  shouldValidate?: boolean
  /**
   * Should this trigger event and change handlers
   *
   * @default false
   */
  shouldTrigger?: boolean
  merge?: boolean
}

export type UseFormSetValue = (
  name: string,
  value: any,
  options?: SetValueOptions
) => void

export type ValidateFn = (
  value: any,
  form: UseFormReturn
) => false | ValidationError | Promise<false | ValidationError>

export type RegisterOptions = {
  value?: any
  validate?: ValidateFn
  shouldTrigger?: boolean
}

export type FieldEventHandler = (
  value: any,
  form: UseFormReturn,
  path: string
) => void

export type FormEventContext<Payload = any> = {
  path: string
  schema?: AnySchema
  payload?: Payload
}

export type FormEventHandler<Payload = any> = (
  event: string,
  context: FormEventContext<Payload>
) => void | Promise<void>

interface FormEventHandlerOptions {
  /**
   * When true handler is triggered when child
   * properties change
   *
   * @default false
   */
  deep?: boolean
}

export type FormEventHandlerDef = {
  hash: number
  options: FormEventHandlerOptions
  handler: FormEventHandler
}

export interface FieldMeta {
  touched: boolean
}

export type FormState = {
  validator?: Schema
  values: Record<string, any>
  meta: {
    isValidating: boolean
    status: Record<string, FormParamStatus>
    globalStatus: Record<string, FormParamStatus>
    context: Record<string, any>
    field: Partial<Record<string, FieldMeta>>
  }
  validators: Record<string, any>
  errors: Record<string, string | undefined>
  globalErrors: Record<string, string | undefined>
  defaultValues: any
  eventHandlers: Record<string, FormEventHandler | undefined>
  changeHandlers: Record<string, FormEventHandlerDef[]>
}

export function useForm(props?: UseFormProps) {
  const debounce = useDebounceByHash(250)
  const nonce = useRef(0)
  const state = useRef(
    ref<FormState>({
      validator: props?.validator,
      values: cloneDeep(props?.defaultValues) || {},
      meta: {
        isValidating: false,
        status: {},
        globalStatus: {},
        field: {},
        context: cloneDeep(props?.context) || {},
      },
      validators: {},
      errors: {},
      globalErrors: {},
      defaultValues: cloneDeep(props?.defaultValues) || {},
      eventHandlers: {},
      changeHandlers: {},
    })
  ).current

  function getNonce(): number {
    const curr = nonce.current
    nonce.current = nonce.current + 1

    return curr
  }

  function getState(path: string) {
    let restrictedPath = path
    if (path.startsWith("state.")) {
      restrictedPath = path.replace(path, "state.")
    }

    return cloneDeep(get(state.value, restrictedPath))
  }

  function addEventHandler(event: string, handler: FormEventHandler) {
    state.value.eventHandlers[event] = handler
  }

  function removeEventHandler(event: string) {
    state.value.eventHandlers[event] = undefined
  }

  async function handleEvent(event: string, context: FormEventContext) {
    const handler = state.value.eventHandlers[event]

    if (!handler) {
      return
    }

    await handler(event, context)
  }

  const setValue: UseFormSetValue = (name, value, config) => {
    if (!config) {
      config = {}
    }

    config.shouldTrigger =
      typeof config?.shouldTrigger === "boolean" ? config.shouldTrigger : true

    if (config?.merge) {
      const updated = name ? get(state.value.values, name) : state.value.values
      merge(updated, value)

      if (name) {
        set(state.value.values, name, updated)
      } else {
        state.value.values = updated
      }
    } else {
      if (!name) {
        state.value.values = value
      } else {
        set(state.value.values, name, value)
      }
    }

    if (config?.shouldTouch) {
      state.value.meta.field[name] = {
        touched: true,
      }
    }

    if (config?.shouldTrigger) {
      void handleEvent("change", {
        path: name,
      })

      void runFieldEventHandlers(name)
    }

    if (config.shouldValidate) {
      debounce(name, async () => {
        await runValidators(name, debounce)
      })
    }
  }

  async function runAllValidators() {
    state.value.errors = {}
    const paths = Object.keys(state.value.validators)
    runGlobalValidator()
    state.value.meta.isValidating = true

    for (const path of paths) {
      const validator = state.value.validators[path]

      if (!validator) {
        continue
      }

      await runFieldValidator(validator, path)
    }

    state.value.meta.isValidating = false
  }

  function runGlobalValidator(): void {
    if (!state.value.validator) {
      return
    }

    const newErrors: Record<string, string | undefined> = {}
    const newStatuses: Record<string, FormParamStatus> = {}

    try {
      state.value.validator.validateSync(state.value.values, {
        abortEarly: false,
        context: state.value.meta.context,
      })
    } catch (err) {
      if (err instanceof ValidationError) {
        for (const error of err.errors) {
          const [path] = error.split(" ")
          newErrors[path] = error
          newStatuses[path] = FormParamStatus.INVALID
        }
      } else {
        console.error(err)
      }
    }

    state.value.globalErrors = newErrors
    state.value.meta.globalStatus = newStatuses
  }

  async function runValidators(
    name: string,
    debounce: UseDebounceByHashReturn
  ) {
    const subPaths = getParentPaths(name)
    debounce("globalValidator", () => {
      runGlobalValidator()
    })
    state.value.meta.isValidating = true

    for (const sub of subPaths) {
      const fieldValidator = get(state.value.validators, sub)
      if (!fieldValidator) {
        continue
      }

      await runFieldValidator(fieldValidator, sub)
    }

    state.value.meta.isValidating = false
  }

  async function runFieldValidator(validator: ValidateFn, name: string) {
    let err: false | ValidationError = false

    try {
      err = await validator(get(state.value.values, name), form)
    } catch (error) {
      if (error instanceof ValidationError) {
        err = error
      } else {
        console.error("Converting unknown error to ValidationError: ", error)

        state.value.errors[name] = "Unknown error"
      }
    }

    if (err) {
      state.value.errors[name] = err.message
      state.value.meta.status[name] = FormParamStatus.INVALID
    } else {
      state.value.errors[name] = undefined
      state.value.meta.status[name] = FormParamStatus.VALID
    }
  }

  function register(name: string, options?: RegisterOptions) {

    options = {
      ...options,
      shouldTrigger:
        typeof options?.shouldTrigger === "boolean"
          ? options?.shouldTrigger
          : true,
    }
    state.value.validators[name] = options?.validate

    if (!state.value.meta.field[name]) {
      state.value.meta.field[name] = {
        touched: false,
      }
    }

    if (isFilled(options?.value)) {
      const curr = cloneDeep(get(state.value.values, name))

      if (curr) {
        const newValue = isPlainObject(curr || options?.value)
          ? mergeWith(curr, options?.value, (objValue, srcValue) =>
            isFilled(srcValue) ? srcValue : objValue
          )
          : isFilled(options?.value)
            ? options?.value
            : curr
        set(state.value.values, name, newValue)
        set(state.value.defaultValues, name, newValue)
      } else {
        set(state.value.values, name, options?.value)
        set(state.value.defaultValues, name, options?.value)
      }
    } else {
      set(
        state.value.values,
        name,
        cloneDeep(get(state.value.defaultValues, name))
      )
      set(
        state.value.defaultValues,
        name,
        cloneDeep(get(state.value.defaultValues, name))
      )
    }

    if (options?.shouldTrigger) {
      void handleEvent("change", {
        path: name,
      })
    }

    return {
      onChange: (value: any) => {
        setValue(name, value, {
          shouldValidate: true,
          shouldTouch: true,
          shouldDirty: true,
        })
      },
      onBlur: (value: any) => {
        setValue(name, value, {
          shouldValidate: true,
          shouldTouch: true,
          shouldDirty: true,
        })
      },
    }
  }

  async function runFieldEventHandlers(name: string) {
    const handlers = state.value.changeHandlers[name]
    if (handlers?.length) {
      for (const handlerDef of handlers) {
        try {
          await handlerDef.handler("change", {
            path: name,
            payload: readonly(get(state.value.values, name)),
          })
        } catch (err) {
          console.error(err)
        }
      }
    }

    const parentPaths = getParentPaths(name)

    for (const path of parentPaths) {
      const handlers = state.value.changeHandlers[path]

      if (!handlers?.length) {
        continue
      }

      for (const handlerDef of handlers) {
        if (handlerDef.options.deep) {
          try {
            await handlerDef.handler("change", {
              path: path,
              payload: readonly(get(state.value.values, path)),
            })
          } catch (err) {
            console.error(err)
          }
        }
      }
    }
  }

  function addChangeHandler(
    name: string,
    handler: FormEventHandler,
    options?: FormEventHandlerOptions
  ): number {
    if (!state.value.changeHandlers[name]) {
      state.value.changeHandlers[name] = []
    }

    const hash = getNonce()
    state.value.changeHandlers[name].push({
      hash: hash,
      handler,
      options: {
        deep: options?.deep,
      },
    })

    return hash
  }

  function removeChangeHandler(name: string, hash: number) {
    if (!state.value.changeHandlers[name]) {
      return
    }

    state.value.changeHandlers[name] = state.value.changeHandlers[name].filter(
      (handler) => handler.hash !== hash
    )
  }

  function getFieldStatus(
    name: string = "",
    options?: { fieldOnly?: boolean }
  ): FormParamStatus {
    for (const field in form.state.value.meta.status) {
      if (!field.startsWith(name)) {
        continue
      }

      const fieldStatus = form.state.value.meta.status[field]
      if (fieldStatus === FormParamStatus.INVALID) {
        return FormParamStatus.INVALID
      }
    }

    if (!options?.fieldOnly) {
      for (const field in form.state.value.meta.globalStatus) {
        if (!field.startsWith(name)) {
          continue
        }

        const fieldStatus = form.state.value.meta.globalStatus[field]
        if (fieldStatus === FormParamStatus.INVALID) {
          return FormParamStatus.INVALID
        }
      }
    }

    return FormParamStatus.VALID
  }

  function getFieldMeta(name: string = "") {
    const section = Object.entries(form.state.value.meta.field)
      .filter(([key]) => key.startsWith(name))
      .map((item) => item[1])

    const meta = section.reduce(
      (acc, curr) => {
        if (curr?.touched) {
          return {
            ...acc,
            touched: true,
          }
        }

        return acc
      },
      { touched: false }
    )

    return meta
  }

  function getValues(name?: string | string[]) {
    if (!name) {
      return cloneDeep(state.value.values)
    }

    if (Array.isArray(name)) {
      return name.map((item) => {
        const found = get(state.value.values, item)
        return found ? cloneDeep(found) : found
      })
    }

    const found = get(state.value.values, name)

    return found ? cloneDeep(found) : found
  }

  function handleSubmit(
    callback: (data: any) => void
  ): React.FormEventHandler<HTMLFormElement> {
    return (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (getFieldStatus() === FormParamStatus.VALID) {
        callback(cloneDeep(state.value.values))
      }
    }
  }

  interface UnRegisterParams {
    keepHandlers?: boolean
  }

  function unregister(params?: UnRegisterParams) {
    state.value = {
      errors: {},
      globalErrors: {},
      meta: {
        isValidating: false,
        status: {},
        globalStatus: {},
        field: {},
        context: {},
      },
      values: {},
      validators: {},
      defaultValues: {},
      eventHandlers: params?.keepHandlers ? state.value.eventHandlers : {},
      changeHandlers: params?.keepHandlers ? state.value.changeHandlers : {},
    }
  }

  interface RemovePathParams {
    keepValidators?: boolean
  }

  function remove(name: string, options?: RemovePathParams): void {
    if (!options) {
      options = {}
    }

    if (typeof options?.keepValidators !== "boolean") {
      options.keepValidators = true
    }

    const parts = toPath(name)
    const parentPath = toPathString(parts.slice(0, parts.length - 1))
    const parent = get(state.value.values, parentPath)

    if (!parent) {
      return
    }

    // Remove the errors too
    Object.keys(state.value.errors)
      .filter((key) => key.startsWith(name))
      .forEach((key) => (state.value.errors[key] = undefined))

    // Remove the global errors too
    Object.keys(state.value.globalErrors)
      .filter((key) => key.startsWith(name))
      .forEach((key) => (state.value.globalErrors[key] = undefined))

    // Remove the statuses
    Object.keys(state.value.meta.status)
      .filter((key) => key.startsWith(name))
      .forEach((key) => delete state.value.meta.status[key])

    if (!options.keepValidators) {
      Object.keys(state.value.validators)
        .filter((key) => key.startsWith(name))
        .forEach((key) => (state.value.validators[key] = undefined))
    }

    const elKey = Number(parts.at(-1))
    const isArrayEl = typeof elKey === "number" && !isNaN(elKey)

    if (isArrayEl) {
      parent.splice(elKey, 1)
      setValue(parentPath, parent, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      })
    } else {
      setValue(name, undefined, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      })
    }
  }

  interface ResetFormProps {
    validator?: UseFormProps["validator"]
    context?: UseFormProps["context"]
    defaultValues?: UseFormProps["defaultValues"]
    mergeDefaultValues?: boolean
  }

  function reset(props?: ResetFormProps) {
    let newDefaultValues = {}
    if (props?.mergeDefaultValues) {
      newDefaultValues = merge(
        state.value.defaultValues,
        cloneDeep(props.defaultValues)
      )
    } else {
      newDefaultValues = cloneDeep(props?.defaultValues)
    }

    state.value = {
      ...state.value,
      validator: props?.validator ? props.validator : state.value.validator,
      defaultValues: newDefaultValues,
      values: newDefaultValues || {},
      meta: {
        isValidating: false,
        status: {},
        globalStatus: {},
        field: {},
        context: props?.context
          ? cloneDeep(props.context)
          : state.value.meta.context,
      },
      errors: {},
    }
  }

  const [form, __setForm] = useState({
    state,
    setValue,
    register,
    remove,
    unregister,
    addEventHandler,
    removeEventHandler,
    handleEvent,
    getFieldStatus,
    getFieldMeta,
    getValues,
    runAllValidators,
    reset,
    getState,
    addChangeHandler,
    removeChangeHandler,

    getFieldErrors(name: string): string | false {
      const found = get(state.value.errors, name)

      if (found) {
        return found
      }

      return false
    },
    handleSubmit: handleSubmit,
  })

  return form
}



export type UseFormReturn = ReturnType<typeof useForm>

export function useFormEmit(form: UseFormReturn) {
  const { handleEvent } = form

  /**
   * Emit an event to the form object
   */
  function emit(event: string, context: FormEventContext) {
    void handleEvent(event, context)
  }

  return emit
}

export type UseWatchProps = {
  form: UseFormReturn
  defaultValue?: any
  name: string | string[]
  deep?: boolean | number
  immediate?: boolean
}

export function useWatch(props: UseWatchProps): any | any[] {
  const { form } = props

  const [piece, setPiece] = useState(
    cloneDeep(form.getValues(props.name) ?? props.defaultValue)
  )

  useEffect(() => {
    const unwatch = watch(
      () => get(form.state.value.values, props.name),
      () => {
        const found = get(
          form.state.value.values,
          props.name,
          props.defaultValue
        )

        setPiece(cloneDeep(found))
      },
      {
        immediate: props.immediate || true,
        deep: props.deep,
      }
    )

    return () => {
      unwatch()
    }
  }, [props.name, form])

  return piece
}

export type UseFieldErrorProps = {
  form: UseFormReturn
  names: string[]
}

export function useFieldError(props: UseFieldErrorProps): (false | string)[] {
  const { form } = props
  const [piece, setPiece] = useState<(false | string)[]>([])

  useEffect(() => {
    const unwatch = watch(
      [
        ...props.names.map((name) => () => form.state.value.errors[name]),
        ...props.names.map((name) => () => form.state.value.globalErrors[name]),
      ],
      () => {
        const found = props.names.map(
          (name) =>
            form.state.value.errors[name] ||
            form.state.value.globalErrors[name] ||
            false
        )
        setPiece(found)
      },
      { immediate: true }
    )

    return () => {
      unwatch()
    }
  }, [form])

  return piece
}

export type UseFieldStatusProps = {
  name?: string
  form: UseFormReturn
  fieldOnly?: boolean
}

export function useFieldStatus(props: UseFieldStatusProps): FormParamStatus {
  const { form, name = "", fieldOnly } = props
  const [piece, setPiece] = useState<FormParamStatus>(FormParamStatus.PENDING)

  useEffect(() => {
    const unwatch = watch(
      [
        () => form.state.value.meta.status,
        () => form.state.value.meta.globalStatus,
        () => form.state.value.meta.isValidating,
      ],
      () => {
        if (form.state.value.meta.isValidating) {
          setPiece(FormParamStatus.INVALID)

          return
        }

        const status = form.getFieldStatus(name, { fieldOnly })
        setPiece(status)
      },
      {
        immediate: true,
        deep: true,
      }
    )

    return () => {
      unwatch()
    }
  }, [props.name, form, fieldOnly])

  return piece
}

export function useFieldMeta(props: UseFieldStatusProps): FieldMeta {
  const { form, name = "" } = props
  const [piece, setPiece] = useState<FieldMeta>({ touched: false })

  useEffect(() => {
    const unwatch = watch(
      () => form.state.value.meta.field,
      () => {
        setPiece(form.getFieldMeta(name) || { touched: false })
      },
      {
        immediate: true,
        deep: true,
      }
    )

    return () => {
      unwatch()
    }
  }, [props.name, form])

  return piece
}

/**
 * Execute callback when a field is changed
 * by user input
 */
export function useFieldChange(props: UseFieldChangeProps) {
  useEffect(() => {
    const hash = props.form.addChangeHandler(
      props.name,
      props.handler,
      props.options
    )

    return () => {
      props.form.removeChangeHandler(props.name, hash)
    }
  }, [props.form, props.name])
}

export interface UseFieldChangeProps {
  name: string
  form: UseFormReturn
  handler: FormEventHandler
  options?: FormEventHandlerOptions
}

function getParentPaths(path: string): string[] {
  const parts = toPath(path)
  const subPaths = []

  let result = ""
  for (const part of parts) {
    const isNumber = Number(part)

    if (typeof isNumber === "number" && !isNaN(isNumber)) {
      result += `[${isNumber}]`
    } else if (!result) {
      result += part
    } else {
      result += `.${part}`
    }

    subPaths.push(result)
  }

  return subPaths
}

export function toPathString(parts: string[]) {

  let result = ""

  for (const part of parts) {
    if (!part) {
      continue
    }

    const numKey = Number(part)

    if (typeof numKey === "number" && !isNaN(numKey)) {
      result += `[${numKey}]`
    } else if (!result) {
      result += part
    } else {
      result += `.${part}`
    }
  }

  return result
}



